import { AxiosError } from 'axios';
import moment from 'moment';
import { UseQueryOptions, useQuery, useQueryClient } from 'react-query';

import {
  PaginatedResponse,
  SerialID,
  StageDetail,
  StageCollection,
  RecruitmentProcessCollection,
  CandidateCollection,
  JobPostingDetail,
} from '../types';

export interface SearchCandidate {
  id: CandidateCollection['id'];
  name: string;
  recruitmentProcess: {
    id: RecruitmentProcessCollection['id'];
    status: RecruitmentProcessCollection['status'];
    stage_type: RecruitmentProcessCollection['stage_type'];
  } | null;
  stage: {
    id: StageDetail['id'];
  } | null;
  ocp: number | null;
  sjt: number | null;
  logic: number | null;
  motivational: number | null;
  raw_potential: number | null;
  operational: number | null;
  match: number | null;
  tags: number[];
  recruiter_visualization_id: number | null;
}

export interface QuestionFilter {
  [questionId: string]: string[];
}

export interface UseSearchCandidatesOptions {
  key?: string;
  search?: string;
  attachmentSearch?: string;
  page: number;
  pageSize: number;
  addressContryIn?: string;
  addressStateIn?: string;
  addressCityIn?: string;
  updatedAtMax?: string;
  updatedAtMin?: string;
  createdAtMax?: string;
  createdAtMin?: string;
  ageStart?: number;
  ageEnd?: number;
  salaryExpectationStart?: number;
  salaryExpectationEnd?: number;
  disabilities?: string;
  candidateSourceOptions?: string;
  sort?: string;
  direction?: string;
  stage?: string;
  jobPostingId?: JobPostingDetail['id'];
  status?: RecruitmentProcessCollection['status'];
  functionId?: SerialID;
  onError?: UseQueryOptions<unknown, AxiosError>['onError'];
  enabled?: UseQueryOptions<unknown, AxiosError>['enabled'];
  matchMin?: string;
  matchMax?: string;
  rawPotentialMin?: string;
  rawPotentialMax?: string;
  ocpMin?: string;
  ocpMax?: string;
  logicMin?: string;
  logicMax?: string;
  sjtMin?: string;
  sjtMax?: string;
  motivationalMin?: string;
  motivationalMax?: string;
  operationalLogic?: string;
  testCompleteness?: 'MISSING' | 'FINALIZED';
  tags?: string[];
  additionalQuestionsCompleteness?: 'MISSING' | 'FINALIZED';
  questionFilters: QuestionFilter;
}

const getOrdering = ({
  direction,
  functionId,
  sort,
  jobPostingId,
}: {
  sort?: string;
  direction?: string;
  functionId?: SerialID;
  jobPostingId?: JobPostingDetail['id'];
}) => {
  if (!sort || direction === undefined) {
    return undefined;
  }

  if (sort === 'match') {
    if (functionId) {
      return `${direction}match,id`;
    }
    return undefined;
  }

  if (
    ['ocp', 'sjt', 'logic', 'motivational', 'raw_potential', 'operational'].includes(sort) &&
    !jobPostingId
  ) {
    return `${direction}recent_${sort},id`;
  }

  if (sort === 'name' && jobPostingId) {
    return `${direction}candidate__${sort},id`;
  }

  return `${direction}${sort},id`;
};

const useSearchCandidates = ({
  onError,
  enabled = true,
  ...options
}: UseSearchCandidatesOptions) => {
  const queryClient = useQueryClient();

  return useQuery<
    PaginatedResponse<SearchCandidate>,
    AxiosError,
    PaginatedResponse<SearchCandidate>,
    [string, UseSearchCandidatesOptions]
  >(['useSearchCandidates', options], {
    async queryFn({ queryKey }) {
      const [
        ,
        {
          search,
          attachmentSearch,
          page,
          pageSize,
          addressContryIn,
          addressCityIn,
          addressStateIn,
          createdAtMax,
          createdAtMin,
          updatedAtMax,
          updatedAtMin,
          ageStart,
          ageEnd,
          salaryExpectationStart,
          salaryExpectationEnd,
          disabilities,
          candidateSourceOptions,
          stage,
          jobPostingId,
          status,
          direction,
          sort,
          functionId,
          logicMax,
          logicMin,
          matchMax,
          matchMin,
          motivationalMax,
          motivationalMin,
          ocpMax,
          ocpMin,
          operationalLogic,
          rawPotentialMax,
          rawPotentialMin,
          sjtMax,
          sjtMin,
          testCompleteness,
          additionalQuestionsCompleteness,
          questionFilters,
          tags,
        },
      ] = queryKey;

      const ordering = getOrdering({ direction, jobPostingId, sort, functionId });

      let birthdateStart = undefined;
      let birthdateEnd = undefined;

      if (ageStart) {
        birthdateEnd = moment().subtract(ageStart, 'years').format('YYYY-MM-DD');
      }

      if (ageEnd) {
        birthdateStart = moment().subtract(ageEnd, 'years').format('YYYY-MM-DD');
      }

      if (jobPostingId) {
        let stageId: SerialID | undefined;

        if (['SUBSCRIBED', 'SELECTED', 'APPROVED', 'HIRED'].includes(stage || '')) {
          const stages = await queryClient.fetchQuery<PaginatedResponse<StageCollection>>([
            '/stages/',
            { params: { job_posting_id: jobPostingId } },
          ]);
          stageId = stages.results.find((s) => s.type === stage)?.id;
        } else if (stage && !isNaN(Number(stage))) {
          stageId = Number(stage);
        }

        const recruitmentProcesses = await queryClient.fetchQuery<
          PaginatedResponse<RecruitmentProcessCollection>
        >([
          '/recruitment-processes/',
          {
            params: {
              search,
              attachment_search: attachmentSearch,
              ...(stageId
                ? {
                    stage_id: stageId,
                  }
                : {
                    job_posting: jobPostingId,
                  }),
              status,
              page,
              page_size: pageSize,
              candidate__address_country__iin: addressContryIn,
              candidate__address_state__iin: addressStateIn,
              candidate__address_city__iin: addressCityIn,
              candidate__updated_at__date__gte: updatedAtMin,
              candidate__updated_at__date__lte: updatedAtMax,
              candidate__birthdate__gte: birthdateStart,
              candidate__birthdate__lte: birthdateEnd,
              candidate__salary_expectation__lte: salaryExpectationEnd,
              candidate__salary_expectation__gte: salaryExpectationStart,
              candidate__disabilities__iin: disabilities,
              candidate__tags__id__in: tags,
              created_at__date__gte: createdAtMin,
              created_at__date__lte: createdAtMax,
              candidate_source_option__in: candidateSourceOptions,
              function_id: functionId,
              ...(functionId ? { match__score__gte: matchMin } : {}),
              ...(functionId ? { match__score__lte: matchMax } : {}),
              ...(functionId && testCompleteness === 'MISSING'
                ? { match__score__isnull: true }
                : {}),
              ...(functionId && testCompleteness === 'FINALIZED'
                ? { match__score__isnull: false }
                : {}),
              logic__gte: logicMin,
              logic__lte: logicMax,
              motivational__gte: motivationalMin,
              motivational__lte: motivationalMax,
              ocp__gte: ocpMin,
              ocp__lte: ocpMax,
              raw_potential__gte: rawPotentialMin,
              raw_potential__lte: rawPotentialMax,
              sjt__gte: sjtMin,
              sjt__lte: sjtMax,
              operational:
                operationalLogic === 'Aprovado'
                  ? '1'
                  : operationalLogic === 'Reprovado'
                  ? '0'
                  : undefined,
              ordering,
              has_answered_questions:
                additionalQuestionsCompleteness === 'FINALIZED'
                  ? true
                  : additionalQuestionsCompleteness === 'MISSING'
                  ? false
                  : undefined,
              question_filters:
                Object.keys(questionFilters).length > 0
                  ? JSON.stringify(
                      Object.entries(questionFilters)
                        .filter(([_, values]) => values[0] !== '')
                        .map(([questionId, values]) => ({
                          question_id: parseInt(questionId, 10),
                          value: values,
                        }))
                    )
                  : undefined,
            },
          },
        ]);

        return {
          ...recruitmentProcesses,
          results: recruitmentProcesses.results.map<SearchCandidate>((recruitmentProcess) => ({
            id: recruitmentProcess.candidate.id,
            name: recruitmentProcess.candidate.name,
            recruitmentProcess: {
              id: recruitmentProcess.id,
              status: recruitmentProcess.status,
              stage_type: recruitmentProcess.stage_type,
            },
            stage: {
              id: recruitmentProcess.stage.id,
            },
            logic: recruitmentProcess.logic,
            motivational: recruitmentProcess.motivational,
            ocp: recruitmentProcess.ocp,
            operational: recruitmentProcess.operational,
            raw_potential: recruitmentProcess.raw_potential,
            sjt: recruitmentProcess.sjt,
            match: recruitmentProcess.match,
            tags: recruitmentProcess.candidate.tags,
            recruiter_visualization_id: recruitmentProcess.recruiter_visualization_id,
          })),
        };
      }

      const candidates = await queryClient.fetchQuery<PaginatedResponse<CandidateCollection>>([
        '/candidates/',
        {
          params: {
            search,
            attachment_search: attachmentSearch,
            page,
            page_size: pageSize,
            address_country__iin: addressContryIn,
            address_state__iin: addressStateIn,
            address_city__iin: addressCityIn,
            updated_at__gte: updatedAtMin,
            updated_at__lte: updatedAtMax,
            created_at__gte: createdAtMin,
            created_at__lte: createdAtMax,
            function_id: functionId,
            ...(functionId ? { match__score__gte: matchMin } : {}),
            ...(functionId ? { match__score__lte: matchMax } : {}),
            recent_logic__gte: logicMin,
            recent_logic__lte: logicMax,
            recent_motivational__gte: motivationalMin,
            recent_motivational__lte: motivationalMax,
            recent_ocp__gte: ocpMin,
            recent_ocp__lte: ocpMax,
            recent_raw_potential__gte: rawPotentialMin,
            recent_raw_potential__lte: rawPotentialMax,
            recent_sjt__gte: sjtMin,
            recent_sjt__lte: sjtMax,
            recent_operational:
              operationalLogic === 'Aprovado'
                ? '1'
                : operationalLogic === 'Reprovado'
                ? '0'
                : undefined,
            birthdate__lte: birthdateEnd,
            birthdate__gte: birthdateStart,
            salary_expectation__lte: salaryExpectationEnd,
            salary_expectation__gte: salaryExpectationStart,
            disabilities__iin: disabilities,
            candidate_source_option__in: candidateSourceOptions,
            tags__id__in: tags,
            ordering,
          },
        },
      ]);

      return {
        ...candidates,
        results: candidates.results.map<SearchCandidate>((candidate) => ({
          id: candidate.id,
          name: candidate.name,
          recruitmentProcess: null,
          stage: null,
          logic: candidate.recent_logic,
          motivational: candidate.recent_motivational,
          ocp: candidate.recent_ocp,
          operational: candidate.recent_operational,
          raw_potential: candidate.recent_raw_potential,
          sjt: candidate.recent_sjt,
          match: candidate.recent_match,
          tags: candidate.tags,
          recruiter_visualization_id: candidate.recruiter_visualization_id,
        })),
      };
    },
    onError,
    enabled,
    keepPreviousData: true,
  });
};

export default useSearchCandidates;
